/**
 * Campo de teléfono con código de país
 * -----------------------------------------------------------------------------
 * Uso:
 *   <div class="form-outline" data-company-phone-init data-company-phone-country="CO">
 *     <input type="tel" class="form-control" id="cel" name="celular" />
 *     <label class="form-label" for="cel">Celular</label>
 *   </div>
 *
 * El campo visible contiene solo el número nacional; el prefijo se elige en un
 * desplegable. Al enviar el formulario lo que viaja es el número completo en
 * formato E.164 (`+573001234567`), a través de un input oculto que hereda el
 * `name` original. Así el backend recibe un único valor sin ambigüedad y no hay
 * que recomponerlo a mano.
 *
 * Sin `name` no se crea el campo oculto: el valor completo queda disponible en
 * `wrapper.__companyPhone.value` y en el evento `valueChanged.company.phone`.
 */

import { COUNTRIES, findCountry } from './countries.js';
import { syncLabel } from './floating-labels.js';
import { phoneES } from './locale-es.js';
import { placePanel, closeOnViewportChange } from './place-panel.js';
import { normalizeText } from './normalize-text.js';

const DEFAULT_COUNTRY = 'CO';

// Compartida con el buscador del menú lateral: los dos filtran texto escrito a
// mano, donde nadie pone tildes.
const normalize = normalizeText;

class PhoneInput {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('input');
    this.menu = null;

    const iso = (wrapper.getAttribute('data-company-phone-country') || DEFAULT_COUNTRY).toUpperCase();
    this.country = findCountry(iso) || findCountry(DEFAULT_COUNTRY);

    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    this._setup();
  }

  _setup() {
    const { wrapper, input } = this;

    input.type = 'tel';
    input.setAttribute('autocomplete', 'tel-national');
    input.setAttribute('inputmode', 'tel');

    // El name pasa al campo oculto, que es el que lleva el número completo.
    if (input.name) {
      this.hidden = document.createElement('input');
      this.hidden.type = 'hidden';
      this.hidden.name = input.name;
      wrapper.appendChild(this.hidden);
      input.removeAttribute('name');
    }

    // El prefijo va en un `.input-group` de MDB, no superpuesto dentro del
    // `.form-outline`. Superponerlo obligaría a desplazar el input, el label
    // flotante y la muesca del borde con un offset medido en runtime (el ancho
    // depende del prefijo: +1 frente a +591). El input-group ya resuelve la
    // maquetación con CSS de MDB, sin medir nada.
    this.group = document.createElement('div');
    this.group.className = 'input-group company-phone';
    wrapper.parentNode.insertBefore(this.group, wrapper);

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    // `input-group-text` y no `btn`: MDB añade `padding-top` SOLO por arriba a
    // los botones outline dentro de un input-group
    // (`.input-group>.btn[class*=btn-outline-]`), y ese padding asimétrico
    // descentra el prefijo. Además así se ve igual que el de moneda.
    this.toggle.className = 'input-group-text company-phone-toggle';
    this.toggle.setAttribute('aria-label', phoneES.selectCountry);
    this.toggle.setAttribute('aria-haspopup', 'listbox');

    this.group.appendChild(this.toggle);
    this.group.appendChild(wrapper);

    this.toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      this._toggleMenu();
    });

    input.addEventListener('input', () => {
      // Se admite solo dígitos y espacios: el prefijo lo pone el desplegable.
      const cleaned = input.value.replace(/[^\d\s]/g, '');
      if (cleaned !== input.value) input.value = cleaned;
      this._sync();
    });

    this._renderToggle();

    // **Por `setValue` y no por `_sync`.** El campo llega del servidor con el
    // número completo —«+573001234567»—, y el input visible guarda SOLO la
    // parte nacional: el prefijo lo pone el desplegable de al lado. Dejándolo
    // entero ahí dentro, `value` le vuelve a anteponer el prefijo y cada ida y
    // vuelta lo duplica. Un registro abierto para editar y guardado sin tocar
    // una tecla salía con «+57573001234567», y a la tercera edición con tres
    // prefijos: el número ya no sirve para llamar y nadie ve por qué.
    this.setValue(input.value);
  }

  /** Número completo en E.164, sin espacios. */
  get value() {
    const national = this.input.value.replace(/\D/g, '');
    return national ? `${this.country.dial}${national}` : '';
  }

  /**
   * Fija el número. Acepta E.164 («+573001234567») o solo la parte nacional.
   *
   * Hace falta un camino propio porque el input visible guarda SOLO la parte
   * nacional: el prefijo lo pone el desplegable de al lado. Escribir ahí el
   * número completo lo deja dentro del texto, y como `value` vuelve a anteponer
   * el prefijo, cada ida y vuelta lo duplica: un registro cargado para editar y
   * guardado sin tocarlo sale con «+57573001234567».
   *
   * @param {string} value
   */
  setValue(value) {
    const texto = value == null ? '' : String(value).trim();

    if (!texto) {
      this.input.value = '';
      this._sync();
      return;
    }

    if (texto.charAt(0) === '+') {
      const digitos = texto.replace(/\D/g, '');

      // El prefijo más LARGO que encaje, no el primero: +1 es de Estados Unidos
      // y +1809 de República Dominicana, así que quedarse con el primero
      // dejaría tres dígitos del país metidos dentro del número.
      const pais = COUNTRIES.filter((c) =>
        digitos.startsWith(String(c.dial).replace('+', ''))
      ).sort((a, b) => String(b.dial).length - String(a.dial).length)[0];

      if (pais) {
        this.country = pais;
        this._renderToggle();
        this.input.value = digitos.slice(String(pais.dial).replace('+', '').length);
        this._sync();
        return;
      }
    }

    this.input.value = texto.replace(/[^\d\s]/g, '');
    this._sync();
  }

  _renderToggle() {
    this.toggle.innerHTML = `<span class="company-phone-dial">${this.country.dial}</span><i class="fas fa-chevron-down"></i>`;
    this.toggle.title = this.country.name;
  }

  _sync() {
    if (this.hidden) this.hidden.value = this.value;

    // El número puede haberlo escrito el componente al limpiar caracteres o al
    // cargar un registro: el label tiene que subir igual.
    syncLabel(this.input);

    this.group.dispatchEvent(
      new CustomEvent('valueChanged.company.phone', {
        bubbles: true,
        detail: { value: this.value, country: this.country },
      })
    );
  }

  _toggleMenu() {
    if (this.menu) this._closeMenu();
    else this._openMenu();
  }

  _openMenu() {
    this.menu = document.createElement('div');
    this.menu.className = 'company-phone-menu';
    this.menu.innerHTML = `
      <div class="company-phone-search">
        <input type="text" class="form-control form-control-sm" placeholder="${phoneES.search}" />
      </div>
      <ul class="company-phone-list" role="listbox"></ul>
    `;

    // Al body, no dentro del `.input-group`: ver el porqué en place-panel.js.
    document.body.appendChild(this.menu);
    this.list = this.menu.querySelector('.company-phone-list');
    this._renderList('');

    // Se coloca después de pintar la lista: antes el panel mide 0 y la decisión
    // de abrir arriba o abajo saldría siempre mal.
    placePanel(this.group, this.menu);
    this._stopWatching = closeOnViewportChange(() => this._closeMenu(), this.menu);

    const search = this.menu.querySelector('input');
    search.addEventListener('click', (event) => event.stopPropagation());
    search.addEventListener('input', () => this._renderList(search.value));
    search.focus();

    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('keydown', this._onKeydown);
  }

  _closeMenu() {
    if (!this.menu) return;

    this.menu.remove();
    this.menu = null;

    if (this._stopWatching) {
      this._stopWatching();
      this._stopWatching = null;
    }

    document.removeEventListener('click', this._onDocumentClick, true);
    document.removeEventListener('keydown', this._onKeydown);
  }

  _renderList(query) {
    const term = normalize(query.trim());
    const matches = COUNTRIES.filter(
      (country) =>
        !term ||
        normalize(country.name).includes(term) ||
        country.dial.includes(term) ||
        country.iso.toLowerCase() === term
    );

    if (!matches.length) {
      this.list.innerHTML = `<li class="company-phone-empty">${phoneES.noResults}</li>`;
      return;
    }

    this.list.innerHTML = matches
      .map(
        (country) => `
        <li role="option" aria-selected="${country.iso === this.country.iso}">
          <button type="button" class="company-phone-option${
            country.iso === this.country.iso ? ' active' : ''
          }" data-iso="${country.iso}">
            <span class="company-phone-name">${country.name}</span>
            <span class="company-phone-code">${country.dial}</span>
          </button>
        </li>`
      )
      .join('');

    this.list.querySelectorAll('.company-phone-option').forEach((option) => {
      option.addEventListener('click', () => {
        this.country = findCountry(option.dataset.iso);
        this._renderToggle();
        this._sync();
        this._closeMenu();
        this.input.focus();
      });
    });
  }

  _onDocumentClick(event) {
    if (!this.menu) return;
    if (this.menu.contains(event.target) || this.toggle.contains(event.target)) return;
    this._closeMenu();
  }

  _onKeydown(event) {
    if (event.key === 'Escape') this._closeMenu();
  }

  dispose() {
    this._closeMenu();

    if (this.hidden) {
      this.input.name = this.hidden.name;
      this.hidden.remove();
    }

    // Se devuelve el `.form-outline` a su sitio y se retira el input-group.
    this.group.parentNode.insertBefore(this.wrapper, this.group);
    this.group.remove();

    delete this.wrapper.__companyPhone;
  }

  static getInstance(wrapper) {
    return wrapper.__companyPhone || null;
  }
}

/**
 * Inicializa los campos de teléfono presentes en `root`.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 */
export function setupPhoneInputs(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-company-phone-init]').forEach((wrapper) => {
    if (wrapper.__companyPhone) return;
    if (!wrapper.querySelector('input')) return;

    wrapper.__companyPhone = new PhoneInput(wrapper);
  });
}

export { PhoneInput };
export default setupPhoneInputs;
